import { Button, Stack, Typography } from '@mui/material';
import { DialogCommentField, SharedDialog } from 'customer-management/customer-details/view-contracts/dialogs';
import { useViewContractsState } from 'customer-management/customer-details/view-contracts/state/view-contracts-state';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDeleteDraftContract, useSendContractNotification, useUpdateContract } from 'shared/mutations';
import { useGetContract } from 'shared/queries';
import { ContractDocument, ContractRouteParams, I2PEnvironment } from 'shared/types';

interface CancelDialogProps {
  contract: ContractDocument;
  environment: I2PEnvironment;
}
export function CancelDialog({ contract, environment }: CancelDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const state = useViewContractsState();
  const deleteDraftContractMutation = useDeleteDraftContract();
  const updateContractMutation = useUpdateContract();
  const notificationMutation = useSendContractNotification();

  const { contractId } = useParams<ContractRouteParams>();
  const { contract: contractToUpdate } = useGetContract(contractId);

  const updateContractStatus = useCallback(async () => {
    if (contractToUpdate) {
      const contractToMutate = { ...contractToUpdate };
      if (contractToMutate.terms.length || contractToMutate.growthTiers || contractToMutate.volumeTiers) {
        contractToMutate.status = 'active';
      } else {
        contractToMutate.status = 'draft';
      }
      contractToMutate.approvalStatus = 'Approved';
      await updateContractMutation.mutateAsync(contractToMutate);
    }
  }, [contractToUpdate, updateContractMutation]);

  const onCancelRequest = useCallback(async () => {
    const comments = state.comments.value;
    await deleteDraftContractMutation.mutateAsync(contract.id);

    updateContractStatus();

    await notificationMutation.mutateAsync({
      contract,
      template: 'cancel',
      comments,
      environment,
    });

    enqueueSnackbar('Contract Cancelled', { variant: 'success' });

    state.merge({ cancelDialog: false, comments: '' });
  }, [
    updateContractStatus,
    deleteDraftContractMutation,
    contract,
    state,
    notificationMutation,
    enqueueSnackbar,
    environment,
  ]);

  const loading = deleteDraftContractMutation.isLoading;

  return (
    <SharedDialog
      open={state.cancelDialog.get()}
      title='Cancel Contract Changes'
      onCancel={() => {
        state.merge({
          cancelDialog: false,
          comments: '',
        });
      }}
      submit={
        <Button onClick={onCancelRequest} disabled={loading} variant='contained' color='secondary'>
          proceed
        </Button>
      }
    >
      <Stack spacing={1}>
        <Typography>You are about to cancel the changes to the contract.</Typography>
        <Typography>Please leave a comment on the reason for this cancellation.</Typography>
        <DialogCommentField />
      </Stack>
    </SharedDialog>
  );
}
